import { useTranslator } from 'tradera/lang/translate';
import MessageIcon from '@tradera/blueprint/theme/icons2/message.svg?jsx';
import { Profile } from 'tradera/components/site-header/profile';
import { useAppSelector } from 'tradera/state/hooks';
import { classNames } from 'tradera/utils/class-names';
import { useGetMessagingStatsQuery } from 'tradera/state/services/messaging-stats-api';
import { useIsFeatureEnabled } from 'tradera/hooks/use-is-feature-enabled';
import { Button } from 'tradera/components/atoms/button/button';

type ProfileLinkProps = {
    handleProfileClick: (e: React.MouseEvent<HTMLElement>) => void;
    menuExpanded: boolean;
    hasLoggedIn: boolean;
};

export const ProfileLink = ({
    handleProfileClick,
    menuExpanded,
    hasLoggedIn,
}: ProfileLinkProps) => {
    const isMessagingEnabled = useIsFeatureEnabled('messaging');
    const { badges } = useAppSelector((state) => state.ui);
    const { memberAlias, isLoggedIn, facebookId, memberHeroImage } =
        useAppSelector((state) => state.member);

    const { messageNotificationCount, badgeNotificationCount } = badges;
    const notificationCount = messageNotificationCount + badgeNotificationCount;

    const { t } = useTranslator();

    const isUserLoggedInAndMessagingEnabled = isLoggedIn && isMessagingEnabled;

    const { data: messagingStatsData } = useGetMessagingStatsQuery(undefined, {
        refetchOnMountOrArgChange: 30,
        skip: !isUserLoggedInAndMessagingEnabled,
    });
    const threadsUnread = messagingStatsData?.threadsUnread || 0;

    const logInText = t('siteHeader_logIn');
    const tooltip = isLoggedIn ? t('siteHeader_myTradera') : logInText;
    const text = isLoggedIn ? t('siteHeader_account') : logInText;

    let badgeClass = 'badge-primary';
    let borderColorClass = 'border-white';
    if (notificationCount > 0) {
        badgeClass = 'badge-dark';
        borderColorClass = 'border-dark';
    }
    const multipleBadges = threadsUnread > 0 && notificationCount > 0;

    const buttonClasses = classNames('z-modal', {
        'slim-header__button': !hasLoggedIn || isLoggedIn,
        'slim-header__sign-up-link': hasLoggedIn && !isLoggedIn,
        'mr-n1 mr-md-0': isLoggedIn,
    });

    const textClass = classNames('slim-header__link-text', {
        'd-none d-lg-inline': !hasLoggedIn || isLoggedIn,
    });

    return (
        <Button
            theme={!hasLoggedIn || isLoggedIn ? 'text' : 'highlight'}
            aria-label={tooltip}
            aria-haspopup="true"
            aria-expanded={menuExpanded ? 'true' : 'false'}
            className={buttonClasses}
            title={tooltip}
            id="profile-button"
            onClick={handleProfileClick}>
            <span className="d-flex gap-1">
                {(!hasLoggedIn || isLoggedIn) && (
                    <Profile
                        isLoggedIn={isLoggedIn}
                        profilePicture={memberHeroImage?.min}
                        facebookId={facebookId}
                        firstLetter={memberAlias?.charAt(0)}
                    />
                )}
                {(threadsUnread > 0 || notificationCount > 0) && (
                    <span
                        className={classNames(
                            'd-flex slim-header__notification-user badge-pill badge-pill-sm font-weight-bold',
                            badgeClass,
                            {
                                'slim-header__notification-multiple':
                                    multipleBadges,
                            },
                        )}>
                        {threadsUnread > 0 && (
                            <span className="d-inline-flex align-items-center">
                                <MessageIcon className="icon icon-sm" />
                                {threadsUnread < 100 ? threadsUnread : '99+'}
                            </span>
                        )}
                        {notificationCount > 0 && (
                            <span
                                className={`d-inline-flex align-items-center ${
                                    threadsUnread > 0
                                        ? `border-left ${borderColorClass} system-count--messaging`
                                        : 'system-count'
                                }`}>
                                {notificationCount}
                            </span>
                        )}
                    </span>
                )}
                <span className={textClass}>{text}</span>
            </span>
        </Button>
    );
};
